import type { SpecsAutoFormFieldMetadataMap } from "./autoFormMetadata";
import { ElementModel } from "./ElementModel";
import { ObservableModelNode } from "./observable";
import type { SpecsModelPartSnapshot } from "./types";
import {
  cloneValue,
  expectObject,
  readBoolean,
  readNullableString,
  readObjectArray,
  readString,
  splitKnown,
} from "./utils";
import type { JsonObject } from "./utils";

export class PartModel extends ObservableModelNode {
  static autoFormFields: SpecsAutoFormFieldMetadataMap = {
    name: { label: "Name", fieldType: "text" },
    mainElement: { label: "Main element", fieldType: "text" },
    required: { label: "Required", fieldType: "boolean" },
    inheritFrom: { label: "Inherit from", fieldType: "text" },
    extra: { label: "Extra JSON", fieldType: "json", minRows: 4 },
  };

  name!: string;
  mainElement!: string;
  required!: boolean;
  inheritFrom!: string | null;
  elements!: ElementModel[];
  extra!: JsonObject;
  private hasRequired = false;
  private hasInheritFrom = false;

  constructor() {
    super();
    this.defineObservableValue("name", "");
    this.defineObservableValue("mainElement", "");
    this.defineObservableValue("required", true);
    this.defineObservableValue("inheritFrom", "");
    this.defineObservableValue("elements", []);
    this.defineObservableValue("extra", {});
  }

  restore(data: unknown) {
    const source = expectObject(data, "part");

    this.hasRequired = "required" in source;
    this.hasInheritFrom = "inheritFrom" in source;
    this.extra = splitKnown(source, [
      "name",
      "mainElement",
      "required",
      "inheritFrom",
      "elements",
    ]);
    this.name = readString(source, "name");
    this.mainElement = readString(source, "mainElement");
    this.required = readBoolean(source, "required", true);
    this.inheritFrom = readNullableString(source, "inheritFrom");
    this.elements = readObjectArray(source, "elements").map((element) =>
      new ElementModel().restore(element)
    );

    return this;
  }

  save(): SpecsModelPartSnapshot {
    const saved: SpecsModelPartSnapshot = {
      ...cloneValue(this.extra),
      name: this.name,
      mainElement: this.mainElement,
      elements: this.elements.map((element) => element.save()),
    };

    if (this.hasRequired || this.required !== true) {
      saved.required = this.required;
    }
    if (this.hasInheritFrom || this.inheritFrom) {
      saved.inheritFrom = this.inheritFrom;
    }

    return saved;
  }

  setMainElement(elementName: string) {
    if (!elementName) {
      this.mainElement = "";
      return true;
    }

    if (!this.elements.some((element) => element.name === elementName)) {
      return false;
    }

    this.mainElement = elementName;
    return true;
  }

  setRequired(required: boolean) {
    const hadRequired = this.hasRequired;
    const previousRequired = this.required;
    this.hasRequired = true;
    this.required = required;
    if (!hadRequired && previousRequired === required) {
      this.markChanged();
    }
  }

  setInheritFrom(partName: string | null) {
    const hadInheritFrom = this.hasInheritFrom;
    const previousInheritFrom = this.inheritFrom;
    this.hasInheritFrom = true;
    this.inheritFrom = partName;
    if (!hadInheritFrom && previousInheritFrom === partName) {
      this.markChanged();
    }
  }

  getElement(elementName: string) {
    return (
      this.elements.find((element) => element.name === elementName) ?? null
    );
  }

  getMainElement() {
    return this.getElement(this.mainElement);
  }

  getInheritanceGroups() {
    return this.getMainElement()?.getInheritanceGroups() ?? [];
  }

  hasInheritanceGroup(groupName: string) {
    return !groupName || this.getInheritanceGroups().includes(groupName);
  }
}
